import { toast } from '@/components/toasts/toast.helpers'

import store from '../../store'
import { fetchingAPI, apiService } from '../../api'
import {
  setActivating,
  setCategories,
  setIntegration,
  setIntegrations,
  setLoading,
  setLogs,
  setOutcomes,
} from './integrations.redux'

const getHealthScore = async (integration) => {
  let healthScore
  try {
    const state = store.getState()
    const { username, organizationid, token } = state.currentUser

    const healthResponse = await fetch(
      `${apiService.remoteHealth}/org/${integration.organization_id}/conversation/health`,
      {
        headers: {
          'Content-Type': 'application/json',
          'auth-type': 'web',
          username,
          organizationid,
          token,
        },
      }
    )
    healthScore = (await healthResponse.json()).health_score
  } catch (err) {
    healthScore = undefined
    console.error('Failed fetching health score', err)
  }
  return healthScore
}

export const fetchIntegrations = () => async (dispatch) => {
  dispatch(setLoading(true))
  try {
    const integrations = await fetchingAPI(`${apiService.core}/api/integration/`, 'GET', dispatch)

    dispatch(setIntegrations(integrations))
    dispatch(setLogs({}))
  } catch (err) {
    toast.error(`Error retrieving integrations`, { cause: err })
  } finally {
    dispatch(setLoading(false))
  }
}

export const fetchIntegration = (id) => async (dispatch) => {
  dispatch(setLoading(true))
  try {
    const integration = await fetchingAPI(
      `${apiService.core}/api/integration/${id}`,
      'GET',
      dispatch
    )
    integration.healthScore = await getHealthScore(integration)
    dispatch(setIntegration(integration))
  } catch (err) {
    toast.error(`Error retrieving integration`, { cause: err })
  } finally {
    dispatch(setLoading(false))
  }
}

export const createIntegration =
  ({ values, push }) =>
  async (dispatch) => {
    try {
      const integration = await fetchingAPI(
        `${apiService.core}/api/integration/`,
        'POST',
        dispatch,
        JSON.stringify({ ...values, active: false })
      )
      dispatch(setIntegration(integration))
      dispatch(fetchIntegration(integration.id))
      push(`/integrations/edit_config/${integration.id}`)
    } catch (err) {
      toast.error(`Error creating integration: ${err.detail}`, { cause: err })
    }
  }

export const deleteIntegration =
  ({ id, push }) =>
  async (dispatch) => {
    try {
      await fetchingAPI(`${apiService.core}/api/integration/${id}`, 'DELETE', dispatch)
      dispatch(fetchIntegrations())
      push(`/integrations`)
    } catch (err) {
      toast.error(`Error deleting integration: ${id}`, { cause: err })
    }
  }

export const activateIntegration =
  ({ id, values }) =>
  async (dispatch) => {
    try {
      dispatch(setActivating(true))
      await fetchingAPI(
        `${apiService.core}/api/integration/${id}/activate`,
        'POST',
        dispatch,
        JSON.stringify(values)
      )
      dispatch(fetchIntegration(id))
      toast.success(`Integration ${values.active ? 'Activated' : 'Deactivated'}`)
      dispatch(setActivating(false))
    } catch (err) {
      dispatch(setActivating(false))
      toast.error(`Error activating integration: ${id}`, { cause: err })
    }
  }

export const updateIntegration =
  ({ id, values }) =>
  async (dispatch) => {
    try {
      await fetchingAPI(
        `${apiService.core}/api/integration/${id}`,
        'PUT',
        dispatch,
        JSON.stringify(values)
      )
      dispatch(fetchIntegration(id))
    } catch (err) {
      toast.error(`Error updating integration: ${id}`, { cause: err })
    }
  }

export const getDDLogs =
  ({ id, values }) =>
  async (dispatch) => {
    dispatch(setLoading(true))
    try {
      const logs = await fetchingAPI(
        `${apiService.core}/api/integration/logs`,
        'POST',
        dispatch,
        JSON.stringify(values)
      )
      dispatch(setLogs(logs))
    } catch (err) {
      toast.error(`Error getting logs for integration: ${id}`, { cause: err })
    } finally {
      dispatch(setLoading(false))
    }
  }

export const resetHealthScore = (integration) => async (dispatch) => {
  try {
    const state = store.getState()
    const { username, organizationid, token } = state.currentUser
    await fetch(`${apiService.remoteHealth}/org/${integration.organization_id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'auth-type': 'web',
        username,
        organizationid,
        token,
      },
    })
  } catch (err) {
    toast.error(`Error resetting integration's health score`, { cause: err })
  }
  dispatch(fetchIntegration(integration.id))
}

export const fetchDistinctOutcomes = (org_id, connector_type, token) => async (dispatch) => {
  dispatch(setLoading(true))
  try {
    const outcomes = await fetchingAPI(
      `${apiService.core}/api/outcomes?org_id=${org_id}&connector_type=${connector_type}&token=${token}`,
      'GET',
      dispatch
    )

    dispatch(setOutcomes(outcomes))
  } catch (err) {
    toast.error(`Error retrieving outcomes`, { cause: err })
  } finally {
    dispatch(setLoading(false))
  }
}

export const updateOutcomes =
  (id_outcome_map, outcome_id_category_id_pairs, org_id, connector_type, token) =>
  async (dispatch) => {
    dispatch(setLoading(true))
    try {
      // eslint-disable-next-line prefer-const
      for (let [key, value] of Object.entries(outcome_id_category_id_pairs)) {
        let temp_value = value
        if (value === '') {
          temp_value = undefined
        }
        id_outcome_map.set(key, { ...id_outcome_map.get(key), ...{ category_id: temp_value } })
        await fetchingAPI(
          `${apiService.core}/api/outcomes/${key}?token=${token}`,
          'PUT',
          dispatch,
          JSON.stringify(id_outcome_map.get(key))
        )
      }
      dispatch(fetchDistinctOutcomes(org_id, connector_type, token))
      toast.success('Saved Outcome-Category mappings')
    } catch (err) {
      toast.error(`Error updating Outcome-Category mappings: ${err}`, { cause: err })
    } finally {
      dispatch(setLoading(false))
    }
  }

export const fetchCategories = (token) => async (dispatch) => {
  dispatch(setLoading(true))
  try {
    const categories = await fetchingAPI(
      `${apiService.core}/api/outcomes/categories?token=${token}`,
      'GET',
      dispatch
    )

    dispatch(setCategories(categories))
  } catch (err) {
    toast.error(`Error retrieving categories`, { cause: err })
  } finally {
    dispatch(setLoading(false))
  }
}
